
import React, { createContext } from 'react';
import { initializeApp, getApp, getApps } from "firebase/app";
import { getDatabase, ref, query, orderByChild, equalTo } from "firebase/database";
import { initializeAuth, getAuth, GoogleAuthProvider, OAuthProvider, signInWithPhoneNumber, PhoneAuthProvider, RecaptchaVerifier, unlink, updatePhoneNumber, linkWithPhoneNumber, browserLocalPersistence, browserPopupRedirectResolver } from "firebase/auth";
import { getStorage, ref as stRef } from "firebase/storage";

// Import conditionnel de getReactNativePersistence uniquement pour React Native
let getReactNativePersistence;
if (typeof document === 'undefined') {
    // On est dans un environnement React Native
    try {
        const authModule = require('firebase/auth');
        getReactNativePersistence = authModule.getReactNativePersistence;
    } catch (e) {
        console.warn('getReactNativePersistence not available');
    }
}

const FirebaseContext = createContext(null);

let firebase = {
    app: null,
    database: null,
    auth: null,
    storage: null,
}   

const createFullStructure = (app, db, auth, storage, config) => {
    
    // If database is not available, return a structure with safe fallbacks
    if (!db) {
        console.warn("Database not available, returning minimal Firebase structure");
        return {
            app: app,
            config: config,
            database: null,
            auth: auth,
            storage: storage,
            authRef: getAuth,
            googleProvider: new GoogleAuthProvider(),
            appleProvider: new OAuthProvider('apple.com'),
            phoneProvider: new PhoneAuthProvider(auth),          
            RecaptchaVerifier: RecaptchaVerifier,
            signInWithPhoneNumber: signInWithPhoneNumber,   
            updatePhoneNumber: updatePhoneNumber,
            unlink: unlink,
            googleCredential: (idToken, accessToken) => GoogleAuthProvider.credential(idToken, accessToken),    
            linkWithPhoneNumber: linkWithPhoneNumber,
            mobileAuthCredential: (verificationId, code) => PhoneAuthProvider.credential(verificationId, code),
            // All database references will be null - actions should check for null before using
            usersRef: null,
            bookingRef: null,
            settingsRef: null,
            languagesRef: null,
            carTypesRef: null,
            eatRestaurantsRef: null,
            eatMenusRef: null,
            eatOrdersRef: null,
            eatCouriersRef: null,
            eatSettingsRef: null,
            // ... all other refs will be null
        };
    }
    
    return {
        app: app,
        config: config,
        database: db,
        auth: auth,
        storage: storage,
        authRef:getAuth,
        googleProvider:new GoogleAuthProvider(),
        appleProvider:new OAuthProvider('apple.com'),
        phoneProvider:new PhoneAuthProvider(auth),          
        RecaptchaVerifier: RecaptchaVerifier,
        signInWithPhoneNumber: signInWithPhoneNumber,   
        updatePhoneNumber:updatePhoneNumber,
        unlink: unlink,
        googleCredential: (idToken, accessToken)=> GoogleAuthProvider.credential(idToken, accessToken),    
        linkWithPhoneNumber: linkWithPhoneNumber,
        mobileAuthCredential: (verificationId,code) => PhoneAuthProvider.credential(verificationId, code),           
        usersRef: db ? ref(db, 'users') : null,
        bookingRef: db ? ref(db, 'bookings') : null,
        cancelreasonRef: db ? ref(db, 'cancel_reason') : null,
        settingsRef: db ? ref(db, 'settings') : null,
        smtpRef: db ? ref(db, "smtpdata") : null,
        smsRef: db ? ref(db, "smsConfig") : null,
        carTypesRef: db ? ref(db, 'cartypes') : null,
        carTypesEditRef:(id) => db ? ref(db, "cartypes/"+ id) : null,
        carDocImage:(id) =>  storage ? stRef(storage, `cartypes/${id}`) : null,     
        promoRef: db ? ref(db, 'promos') : null,
        promoEditRef:(id) => db ? ref(db, "promos/"+ id) : null,
        eatPromosRef: db ? ref(db, 'eat/promos') : null,
        eatPromoEditRef:(id) => db ? ref(db, "eat/promos/"+ id) : null,
        notifyRef: db ? ref(db,"notifications/") : null,
        notifyEditRef:(id) => db ? ref(db, "notifications/"+ id) : null,
        addressRef: (uid,id) => db ? ref(db, "savedAddresses/"+ uid + "/" + id) : null,
        addressEditRef:(uid) => db ? ref(db, "savedAddresses/"+ uid) : null,
        singleUserRef:(uid) => db ? ref(db, "users/" + uid) : null,
        profileImageRef:(uid) => storage ? stRef(storage,`users/${uid}/profileImage`) : null,
        verifyIdImageRef:(uid) => storage ? stRef(storage,`users/${uid}/verifyIdImage`) : null,
        bookingImageRef:(bookingId,imageType) => storage ? stRef(storage,`bookings/${bookingId}/${imageType}`) : null,
        driversRef: db ? query(ref(db, "users"), orderByChild("usertype"), equalTo("driver")) : null,
        driverDocsRef:(uid) => storage ? stRef(storage,`users/${uid}/license`) : null,       
        driverDocsRefBack:(uid) => storage ? stRef(storage,`users/${uid}/licenseBack`) : null,
        // Nouveaux documents chauffeur
        idImageRef:(uid) => storage ? stRef(storage,`users/${uid}/idImage`) : null,
        idImageBackRef:(uid) => storage ? stRef(storage,`users/${uid}/idImageBack`) : null,
        vtcCardRef:(uid) => storage ? stRef(storage,`users/${uid}/vtcCard`) : null,
        vtcCardBackRef:(uid) => storage ? stRef(storage,`users/${uid}/vtcCardBack`) : null,
        vtcStickerRef:(uid) => storage ? stRef(storage,`users/${uid}/vtcSticker`) : null,
        vtcRegistryRef:(uid) => storage ? stRef(storage,`users/${uid}/vtcRegistry`) : null,
        medicalCertificateRef:(uid) => storage ? stRef(storage,`users/${uid}/medicalCertificate`) : null,
        kbisRef:(uid) => storage ? stRef(storage,`users/${uid}/kbis`) : null,
        vehicleInsuranceRef:(uid) => storage ? stRef(storage,`users/${uid}/vehicleInsurance`) : null,
        rcProRef:(uid) => storage ? stRef(storage,`users/${uid}/rcPro`) : null,
        eatRcProRef:(uid) => storage ? stRef(storage,`users/${uid}/eatRcPro`) : null,
        vehicleRegistrationRef:(uid) => storage ? stRef(storage,`users/${uid}/vehicleRegistration`) : null,
        technicalInspectionRef:(uid) => storage ? stRef(storage,`users/${uid}/technicalInspection`) : null,
        singleBookingRef:(bookingKey) => db ? ref(db, "bookings/" + bookingKey) : null,
        requestedDriversRef:(bookingKey ) => db ? ref(db, "bookings/" + bookingKey  + "/requestedDrivers") : null,
        referralIdRef:(referralId) => db ? query(ref(db, "users"), orderByChild("referralId"), equalTo(referralId)) : null,
        trackingRef: (bookingId) => db ? ref(db, 'tracking/' + bookingId) : null,
        tasksRef:() => db ? query(ref(db, 'bookings'), orderByChild('status'), equalTo('NEW')) : null,
        singleTaskRef:(uid,bookingId) => db ? ref(db, "bookings/" + bookingId  + "/requestedDrivers/" + uid) : null,
        bookingListRef:(uid,role) => 
            !db ? null :
            role == 'customer'? query(ref(db, 'bookings'), orderByChild('customer'), equalTo(uid)):
                (role == 'driver'? 
                    query(ref(db, 'bookings'), orderByChild('driver'), equalTo(uid))
                    :
                    (role == 'fleetadmin'? 
                        query(ref(db, 'bookings'), orderByChild('fleetadmin'), equalTo(uid))
                        : ref(db, 'bookings')
                    )
                ),
        chatRef:(bookingId) => db ? ref(db, 'chats/' + bookingId + '/messages') : null,
        withdrawRef: db ? ref(db, 'withdraws/') : null,
        languagesRef: db ? ref(db, "languages") : null,
        languagesEditRef:(id) => db ? ref(db, "languages/"+ id) : null,
        langEditRef:(id) => db ? ref(db, `languages/${id}/keyValuePairs/`) : null,
        walletHistoryRef:(uid) => db ? ref(db, "walletHistory/" + uid) : null,  
        userNotificationsRef:(uid) => db ? ref(db, "userNotifications/"+ uid) : null,
        userRatingsRef:(uid) => db ? ref(db, "userRatings/"+ uid) : null,
        carsRef:(uid,role) => !db ? null : role == 'driver'? 
            query(ref(db, 'cars'), orderByChild('driver'), equalTo(uid))
            :(role == 'fleetadmin'? 
                query(ref(db, 'cars'), orderByChild('fleetadmin'), equalTo(uid))
                : ref(db, 'cars')
            ),
        carAddRef: db ? ref(db, "cars") : null,
        carEditRef:(id) => db ? ref(db, "cars/"+ id) : null,
        carImage:(id) => storage ? stRef(storage,`cars/${id}`) : null,   
        allLocationsRef: db ? ref(db, "locations") : null,
        userLocationRef:(uid) => db ? ref(db, "locations/"+ uid) : null,
        sosRef: db ? ref(db, 'sos') : null,
        editSosRef:(id) => db ? ref(db, "sos/" +id) : null,
        complainRef: db ? ref(db, 'complain') : null,
        editComplainRef:(id) => db ? ref(db, "complain/" +id) : null,
        paymentSettingsRef: db ? ref(db, "payment_settings") : null,
        usedreferralRef: db ? ref(db,'usedreferral') : null,
        eatRootRef: db ? ref(db, 'eat') : null,
        eatRestaurantsRef: db ? ref(db, 'eat/restaurants') : null,
        eatRestaurantRef:(restaurantId) => db ? ref(db, 'eat/restaurants/' + restaurantId) : null,
        eatRestaurantOwnersRef:(uid) => db ? ref(db, 'eat/restaurantOwners/' + uid) : null,
        eatMenusRef: db ? ref(db, 'eat/menus') : null,
        eatRestaurantMenuRef:(restaurantId) => db ? ref(db, 'eat/menus/' + restaurantId) : null,
        eatOrdersRef: db ? ref(db, 'eat/orders') : null,
        eatOrderRef:(orderId) => db ? ref(db, 'eat/orders/' + orderId) : null,
        eatCustomerOrdersRef:(uid) => db ? ref(db, 'eat/ordersByCustomer/' + uid) : null,
        eatCustomerOrderRef:(uid, orderId) => db ? ref(db, 'eat/ordersByCustomer/' + uid + '/' + orderId) : null,
        eatRestaurantOrdersRef:(restaurantId) => db ? ref(db, 'eat/ordersByRestaurant/' + restaurantId) : null,
        eatRestaurantOrderRef:(restaurantId, orderId) => db ? ref(db, 'eat/ordersByRestaurant/' + restaurantId + '/' + orderId) : null,
        eatCourierOrdersRef:(uid) => db ? ref(db, 'eat/ordersByCourier/' + uid) : null,
        eatCourierOrderRef:(uid, orderId) => db ? ref(db, 'eat/ordersByCourier/' + uid + '/' + orderId) : null,
        eatRatingsRef: db ? ref(db, 'eat/ratings') : null,
        eatRestaurantRatingsRef:(restaurantId) => db ? ref(db, 'eat/ratings/restaurants/' + restaurantId) : null,
        eatCourierRatingsRef:(uid) => db ? ref(db, 'eat/ratings/couriers/' + uid) : null,
        eatCouriersRef: db ? ref(db, 'eat/couriers') : null,
        eatCourierRef:(uid) => db ? ref(db, 'eat/couriers/' + uid) : null,
        eatCourierLocationRef:(uid) => db ? ref(db, 'eat/courierLocations/' + uid) : null,
        eatZonesRef: db ? ref(db, 'eat/zones') : null,
        eatSettingsRef: db ? ref(db, 'eat/settings') : null,
        eatLedgerRef: db ? ref(db, 'eat/ledger') : null,
    }
}

const FirebaseProvider  = ({ config, children, AsyncStorage, token }) => {
    let app, auth, database, storage;
    let authPersistenceUsed = false;

    if (!getApps().length) {
        try {
            app = initializeApp(config);

            if (typeof document !== 'undefined') {
                // Web environment
                auth = initializeAuth(app, {
                    persistence: browserLocalPersistence,
                    popupRedirectResolver: browserPopupRedirectResolver,
                });
            }
            else{
                // React Native environment
                console.log("Initializing Firebase Auth for React Native with AsyncStorage:", !!AsyncStorage);
                // Use AsyncStorage only if explicitly provided to avoid requiring native modules
                if (AsyncStorage && typeof getReactNativePersistence === 'function') {
                    try {
                        auth = initializeAuth(app, {
                            persistence: getReactNativePersistence(AsyncStorage)
                        });
                        authPersistenceUsed = true;
                        console.log("Firebase Auth initialized with provided AsyncStorage persistence");
                    } catch (e) {
                        console.warn('configureFirebase: initializeAuth with provided AsyncStorage failed', e.message);
                        auth = getAuth(app);
                    }
                } else {
                    // Fallback to default auth if AsyncStorage is not provided or persistence helper missing
                    console.warn("AsyncStorage persistence not provided; auth state will not persist between sessions");
                    auth = getAuth(app);
                }
            }
            
            // Initialize database with error handling
            try {
                console.log("Initializing Firebase Database with URL:", config.databaseURL);
                database = getDatabase(app, config.databaseURL);
                console.log("Firebase Database initialized successfully");
                
                // Test basic connectivity with timeout
                setTimeout(async () => {
                    try {
                        const testRef = ref(database, '.info/connected');
                        console.log("Testing database connectivity...");
                    } catch (connError) {
                        console.warn("Database connection test failed:", connError.message);
                    }
                }, 1000);
                
            } catch (error) {
                console.error("Database initialization error:", error);
                database = null;
            }
            
            storage = getStorage(app);
        } catch (error) {
            console.error("Error initializing app:", error);
            // Ensure we still have some valid objects
            database = null;
        }
    } else {
        app = getApp();
        auth = getAuth(app);
        try {
            database = getDatabase(app, config.databaseURL);
        } catch (error) {
            console.error("Database initialization error:", error);
            database = null;
        }
        storage = getStorage(app);
    }

    console.log("Creating Firebase structure with database:", database ? "available" : "null");
    firebase = createFullStructure(app, database, auth, storage, config);
    console.log("Firebase structure created successfully");

    // Diagnostic logging: expose details about auth and phone provider to help debug verifyPhoneNumber issues
    try {
        console.log("Firebase diagnostics: auth present=", !!auth, "authPersistenceUsed=", authPersistenceUsed);
        if (auth && auth.currentUser) {
            console.log("Firebase diagnostics: auth.currentUser.uid=", auth.currentUser.uid);
        }
        if (firebase && firebase.phoneProvider) {
            try {
                const provider = firebase.phoneProvider;
                console.log('Firebase diagnostics: phoneProvider exists, type=', typeof provider);
                // list own property names
                const own = Object.getOwnPropertyNames(provider || {});
                console.log('Firebase diagnostics: phoneProvider ownKeys=', own);
                console.log('Firebase diagnostics: phoneProvider.verifyPhoneNumber typeof=', typeof (provider.verifyPhoneNumber));
                // attempt to stringify a small sample safely
                try {
                    console.log('Firebase diagnostics: phoneProvider sample=', provider && provider.constructor && provider.constructor.name);
                } catch (sErr) {
                    console.log('Firebase diagnostics: error reading phoneProvider sample', sErr);
                }
            } catch (pErr) {
                console.log('Firebase diagnostics: error inspecting phoneProvider', pErr);
            }
        } else {
            console.log('Firebase diagnostics: phoneProvider is null or undefined');
        }
    } catch (dErr) {
        console.log('Firebase diagnostics: unexpected error', dErr);
    }

    return (
        <FirebaseContext.Provider value={firebase}>
            {children}
        </FirebaseContext.Provider>
    )
}

export {
    firebase,
    FirebaseProvider,
    FirebaseContext
}